import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import PaymentForm from './PaymentForm';
import { sortBills, ariaSortFor as ariaSortForColumn, BillSortKey } from './billsTable';

interface Payment {
  id: string;
  amount: number;
  status: string;
  dueDate: string;
  invoiceNumber?: string;
  description?: string;
  payabliToken?: string;
  payabliEntryId?: string;
  currency?: string;
  paymentType?: string;
  createdAt?: string;
  updatedAt?: string;
  statementDate: string;
  paymentDate?: string;
  paymentIdentity?: {
    id: string;
    email: string;
    phone: string | null;
    firstName: string;
    lastName: string;
    uniqueId: string;
    guarantorLastName: string;
  };
  totalDueAmount?: number;
}

interface UserInfo {
  name: string;
  gn: string;
}

interface PaymentAccordionProps {
  payments: Payment[];
  userInfo: UserInfo;
  expandedPaymentId: string | null;
  onPaymentSelect: (payment: Payment) => void;
  onHideDetails: (e: React.MouseEvent) => void;
  loadPayabliScript: (payment: any) => void;
}

/** Direction indicator on a sortable header. Dimmed when that column is not the active sort. */
const SortCaret: React.FC<{ active: boolean; asc: boolean }> = ({ active, asc }) => (
  <span className={`ml-1 text-[10px] leading-none ${active ? 'opacity-100' : 'opacity-30'}`} aria-hidden="true">
    {active ? (asc ? '\u25B2' : '\u25BC') : '\u25BE'}
  </span>
);

const PaymentAccordion: React.FC<PaymentAccordionProps> = ({
  payments,
  userInfo,
  expandedPaymentId,
  onPaymentSelect,
  onHideDetails,
  loadPayabliScript,
}) => {
  const { theme } = useTheme();
  const isStLuke = theme.client === 'stluke';

  // Column sorting (Joel, Aug 2026). The service dates were not in chronological order, so the
  // three data columns are sortable and the table opens on newest service date first.
  // The comparators live in ./billsTable so they can be unit-tested without a Payabli script and a
  // verified session, the same reason installmentPlan.ts was extracted. See billsTable.test.ts.
  const [sortKey, setSortKey] = React.useState<BillSortKey>('date');
  const [sortAsc, setSortAsc] = React.useState<boolean>(false);

  const toggleSort = (key: BillSortKey) => {
    if (key === sortKey) {
      setSortAsc(!sortAsc);
      return;
    }
    setSortKey(key);
    // Account numbers read naturally ascending; dates and amounts are most useful largest first.
    setSortAsc(key === 'account');
  };

  // Screen readers get the sort state from aria-sort. Without it the direction lives only in the
  // caret, which is aria-hidden, so a screen-reader user could sort but not tell which column was
  // active or which way. This grid is divs rather than a real table, hence role="columnheader" on
  // the wrapper rather than the attribute on a <th>.
  const ariaSortFor = (column: BillSortKey) => ariaSortForColumn(sortKey, column, sortAsc);

  const headerBtnClass = `flex items-center text-left w-full rounded focus:outline-none focus:ring-2 focus:ring-offset-1 ${
    isStLuke ? 'hover:text-[#71001F] focus:ring-[#71001F]' : 'hover:text-blue-700 focus:ring-blue-500'
  }`;

  const sortedPayments = React.useMemo(
    () => sortBills(payments, sortKey, sortAsc),
    [payments, sortKey, sortAsc]
  );

  return (
    <div className="mt-6 w-full">
      {/* Table Structure */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden w-full">
        {/* Table Header */}
        <div className={`px-6 py-5 border-b border-slate-200 ${isStLuke ? 'bg-[#F2F5F9]' : 'bg-gradient-to-r from-blue-50 to-indigo-50'}`}>
          <div role="row" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm font-semibold text-slate-700">
            <div role="columnheader" aria-sort={ariaSortFor('account')}>
              <button type="button" onClick={() => toggleSort('account')} className={headerBtnClass} aria-label="Sort by account number">
                <svg className={`w-4 h-4 mr-2 ${isStLuke ? 'text-[#71001F]' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Account Number
                <SortCaret active={sortKey === 'account'} asc={sortAsc} />
              </button>
            </div>
            <div role="columnheader" aria-sort={ariaSortFor('date')}>
              <button type="button" onClick={() => toggleSort('date')} className={headerBtnClass} aria-label="Sort by service date">
                <svg className={`w-4 h-4 mr-2 ${isStLuke ? 'text-[#71001F]' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Service Date
                <SortCaret active={sortKey === 'date'} asc={sortAsc} />
              </button>
            </div>
            <div role="columnheader" aria-sort={ariaSortFor('amount')}>
              <button type="button" onClick={() => toggleSort('amount')} className={headerBtnClass} aria-label="Sort by amount">
                {/* Use a text $ so it aligns with other header icons */}
                <span className={`inline-flex items-center justify-center w-4 h-4 mr-2 text-sm font-bold leading-none ${isStLuke ? 'text-[#71001F]' : 'text-blue-600'}`}>$</span>
                Amount
                <SortCaret active={sortKey === 'amount'} asc={sortAsc} />
              </button>
            </div>
          </div>
        </div>

        {/* Table Body */}
        <div className="divide-y divide-slate-200">
          {sortedPayments.map((payment, index) => (
            <div key={payment.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} ${isStLuke ? 'hover:bg-slate-100' : 'hover:bg-indigo-50'} transition-colors duration-150`}>
              {/* Table Row */}
              <div className="px-6 py-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-center">
                  {/* Account Number */}
                  <div className="flex flex-col">
                    <span className="text-xs text-slate-500 mb-1 sm:hidden">Account Number</span>
                    <div className="text-sm text-slate-800 font-medium truncate">
                      {payment.invoiceNumber ? `#${payment.invoiceNumber}` : `#${payment.id.slice(-8)}`}
                    </div>
                  </div>
                  
                  {/* Service Date (this value has always been the account's date of service) */}
                  <div className="flex flex-col">
                    <span className="text-xs text-slate-500 mb-1 sm:hidden">Service Date</span>
                    <div className="text-sm text-slate-600">
                      {new Date(payment.statementDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </div>
                  </div>
                  
                  {/* Amount */}
                  <div className="flex flex-col">
                    <span className="text-xs text-slate-500 mb-1 sm:hidden">Amount</span>
                    <div className={`text-lg font-bold ${isStLuke ? 'text-[#71001F]' : 'text-blue-600'}`}>
                      {payment.totalDueAmount != null && payment.totalDueAmount < 0
                        ? `-$${Math.abs(payment.totalDueAmount).toLocaleString()}`
                        : `$${payment.totalDueAmount?.toLocaleString() || '0.00'}`}
                    </div>
                  </div>
                </div>
              </div>

              {/* Expanded Payment Form */}
              {expandedPaymentId === payment.id && (
                <div className="border-t border-slate-200 bg-slate-50">
                  <div className="px-6 py-4">
                    <div className="mb-3 flex items-center justify-between">
                      <h4 className="text-md font-medium text-slate-800">Payment Details</h4>
                      <button
                        onClick={onHideDetails}
                        className="text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <PaymentForm
                      payment={payment}
                      userInfo={userInfo}
                      loadPayabliScript={(paymentObj) => loadPayabliScript(paymentObj)}
                      isPayButtonVisible={false}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PaymentAccordion; 